import { useFlickity } from '@/components/hooks/useFlickity'
import { BaseResults } from 'libs/searchspring/components/recommendations/BaseResults'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useState, useEffect, useCallback } from 'preact/hooks'
import clsx from 'clsx'

const KLAVIYO_WIDGET_SELECTOR = '.kl_reviews__star_rating_widget, .kl_reviews__stars_badge'
const MAX_REVEAL_MS = 600
const REVEAL_AFTER_RATING_MS = 50
const RATING_OBSERVER_MS = 10000

export function CarouselResults({
  className,
  wrapperClassName = '',
  containerClassName,
  options = { pageDots: true, cellAlign: 'left' },
  productCardSettings,
  'data-dl-section-id': dataDlSectionId
}) {
  const { results, isLoading } = useSearchspringContext()
  const [skeletonNode, setSkeletonNode] = useState()
  const [realNode, setRealNode] = useState()
  const [containerNode, setContainerNode] = useState()
  const [showRealCarousel, setShowRealCarousel] = useState(false)

  useFlickity(skeletonNode, options, [])
  const { SliderPortal, flickity } = useFlickity(realNode, options, [results])

  const hasData = !isLoading && results?.results

  const resizeCarousel = useCallback(() => {
    if (!flickity) return
    flickity.resize()
    flickity.reloadCells()
  }, [flickity])

  const syncLockedHeight = useCallback(() => {
    if (!containerNode) return
    const height = containerNode.getBoundingClientRect().height
    containerNode.style.height = `${height}px`
  }, [containerNode])

  useEffect(() => {
    if (!hasData || !flickity || !containerNode) {
      setShowRealCarousel(false)
      return
    }

    let revealed = false
    let revealTimer

    const revealCarousel = () => {
      if (revealed) return
      revealed = true
      containerNode.style.height = ''
      setShowRealCarousel(true)
      requestAnimationFrame(resizeCarousel)
    }

    const scheduleReveal = (delay = REVEAL_AFTER_RATING_MS) => {
      clearTimeout(revealTimer)
      revealTimer = setTimeout(revealCarousel, delay)
    }

    const handleRatingsChange = () => {
      if (!containerNode.querySelector(KLAVIYO_WIDGET_SELECTOR)) return
      if (!revealed) {
        syncLockedHeight()
      }
      resizeCarousel()
      if (!revealed) {
        scheduleReveal()
      }
    }

    syncLockedHeight()
    resizeCarousel()

    const ratingObserver = new MutationObserver(handleRatingsChange)
    ratingObserver.observe(containerNode, { subtree: true, childList: true })

    if (containerNode.querySelector(KLAVIYO_WIDGET_SELECTOR)) {
      scheduleReveal()
    } else {
      revealTimer = setTimeout(revealCarousel, MAX_REVEAL_MS)
    }

    const ratingObserverCleanup = setTimeout(() => {
      ratingObserver?.disconnect()
    }, RATING_OBSERVER_MS)

    return () => {
      clearTimeout(revealTimer)
      clearTimeout(ratingObserverCleanup)
      ratingObserver?.disconnect()
      setShowRealCarousel(false)
    }
  }, [hasData, flickity, containerNode, resizeCarousel, syncLockedHeight])

  useEffect(() => {
    if (!showRealCarousel || !flickity || !containerNode) return

    const handleRatingsChange = () => {
      if (containerNode.querySelector(KLAVIYO_WIDGET_SELECTOR)) {
        resizeCarousel()
      }
    }

    const ratingObserver = new MutationObserver(handleRatingsChange)
    ratingObserver.observe(containerNode, { subtree: true, childList: true })

    const cleanup = setTimeout(() => {
      ratingObserver.disconnect()
    }, RATING_OBSERVER_MS)

    return () => {
      ratingObserver.disconnect()
      clearTimeout(cleanup)
    }
  }, [showRealCarousel, flickity, containerNode, resizeCarousel])

  return (
    <BaseResults
      className={className}
      wrapperClassName={wrapperClassName}
      {...(containerClassName && { containerClassName })}
      productCardSettings={productCardSettings}
      renderWrapper={(skeletonItems, realItems) => (
        <div ref={setContainerNode} className="relative w-full grid grid-cols-1 grid-rows-1 transition-all">
          <div className={clsx(
            'w-full row-start-1 col-start-1 transition-opacity',
            !showRealCarousel ? 'block' : 'hidden pointer-events-none'
          )}>
            <div
              ref={setSkeletonNode}
              className={clsx(
                'flickity-container flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625'
              )}
            >
              {skeletonItems}
            </div>
          </div>

          <div className={clsx(
            'w-full row-start-1 col-start-1 transition-opacity',
            showRealCarousel ? 'block' : 'hidden pointer-events-none'
          )}>
            <div
              ref={setRealNode}
              className={clsx(
                'flickity-container flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625'
              )}
              data-dl-section-id={dataDlSectionId}
            >
              <SliderPortal>
                {realItems}
              </SliderPortal>
            </div>
          </div>
        </div>
      )}
    />
  )
}
